// src/pages/TicketsPage.tsx — Phase 8 / Slice E.
//
// Read-only Edmondson-styled comp-ticket summary. One GET /api/tickets call
// returns allocation + visit history + member identity in a single payload.
//
// Slice E divergences from the spec applied here:
//   #2 — history rows show "{date} · {N} tickets" only (no service name)
//   #3 — status pill omitted (every visit is implicitly "used")
//   #4 — pax field is NoOfTickets per visit
//   #7 — pending always 0; no hatched stubs rendered in V1
//   #8 — membershipType comes back null; the type line is hidden
//   #10 — current NYMR year only; no year selector

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import {
  Card,
  DoubleRule,
  Eyebrow,
  Hairline,
  Icons,
  Stamp,
  TopBar,
} from "../components/ui";

// ---------------------------------------------------------------------------
// Types — mirror /api/tickets
// ---------------------------------------------------------------------------

type VisitStatus = "used";

interface HistoryItem {
  id: number;
  service: string | null;
  date: string;
  pax: number;
  status: VisitStatus;
}

interface MemberBlock {
  memberIdNumber: number;
  displayName: string;
  membershipType: string | null;
}

interface TicketsResponse {
  year: string;
  /** Slice F — full list of NYMR years with visits, newest first. */
  availableYears: string[];
  allowance: number;
  used: number;
  pending: number;
  available: number;
  warningOverdrawn: boolean;
  lastSyncedAt: string | null;
  member: MemberBlock;
  history: HistoryItem[];
}

interface NotLinkedResponse {
  memberNotLinked: true;
}

type ApiResult = TicketsResponse | NotLinkedResponse;

function isNotLinked(r: ApiResult): r is NotLinkedResponse {
  return (r as NotLinkedResponse).memberNotLinked === true;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TicketsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ApiResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleDismissed, setStaleDismissed] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  // Re-fetch whenever the user picks a different year. selectedYear === null
  // means "let the server resolve current" (the first-load case).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = selectedYear
      ? `/api/tickets?year=${encodeURIComponent(selectedYear)}`
      : "/api/tickets";
    apiFetch<ApiResult>(url)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Couldn't load your tickets.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const sub = !loading && data && !isNotLinked(data) ? `${data.year} ALLOCATION` : undefined;
  const isStale = !loading && data && !isNotLinked(data) ? isOlderThan8h(data.lastSyncedAt) : false;

  return (
    <div style={{ paddingBottom: 32 }}>
      <TopBar title="Comp Tickets" sub={sub} onBack={() => navigate(-1)} />
      <div style={{ padding: "14px 18px 0", display: "flex", flexDirection: "column", gap: 18 }}>
        {loading && <SkeletonStack />}
        {!loading && error && <ErrorCard message={error} />}
        {!loading && !error && data && isNotLinked(data) && <NotAMemberPanel />}
        {!loading && !error && data && !isNotLinked(data) && (
          <>
            {data.availableYears.length > 1 && (
              <YearSelector
                years={data.availableYears}
                current={data.year}
                onChange={(y) => setSelectedYear(y)}
              />
            )}
            <HeroTicket data={data} />
            {isStale && !staleDismissed && (
              <StaleStrip
                lastSyncedAt={data.lastSyncedAt}
                onDismiss={() => setStaleDismissed(true)}
              />
            )}
            <HistoryCard year={data.year} history={data.history} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YearSelector — pill row above the hero ticket. Slice F.
// ---------------------------------------------------------------------------

function YearSelector({
  years,
  current,
  onChange,
}: {
  years: string[];
  current: string;
  onChange: (y: string) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Allocation year"
      style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
    >
      {years.map((y) => {
        const active = y === current;
        return (
          <button
            key={y}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(y)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              border: active ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
              background: active ? C.brassBg : C.card,
              color: active ? C.brassDk : C.inkSoft,
              fontFamily: F.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
              cursor: "pointer",
            }}
          >
            {y}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroTicket — Edmondson-styled allocation card
// ---------------------------------------------------------------------------

const NOTCH = 16;

function HeroTicket({ data }: { data: TicketsResponse }) {
  return (
    <section
      aria-labelledby="comp-tickets-heading"
      style={{
        position: "relative",
        background: C.paperWarm,
        border: `1px solid ${C.brassLt}`,
        borderRadius: 18,
        boxShadow: C.shadowM,
        overflow: "visible",
        padding: 0,
      }}
    >
      <h1
        id="comp-tickets-heading"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          border: 0,
        }}
      >
        Your complimentary tickets for {data.year}
      </h1>

      {/* notches */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: NOTCH,
          height: NOTCH,
          left: -NOTCH / 2,
          top: "50%",
          marginTop: -NOTCH / 2,
          borderRadius: NOTCH,
          background: C.paper,
          boxShadow: `inset 1px 0 0 ${C.hairlineLt}`,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: NOTCH,
          height: NOTCH,
          right: -NOTCH / 2,
          top: "50%",
          marginTop: -NOTCH / 2,
          borderRadius: NOTCH,
          background: C.paper,
          boxShadow: `inset -1px 0 0 ${C.hairlineLt}`,
        }}
      />

      <div style={{ padding: "18px 22px 14px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontFamily: F.mono,
              fontSize: 11,
              color: C.inkMute,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            YOUR ALLOCATION
          </div>
          <Stamp tone="brass">NYMR · {data.year}</Stamp>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span
            aria-label={`${data.available} tickets available`}
            style={{
              fontFamily: F.serif,
              fontSize: 56,
              color: C.brassDk,
              fontWeight: 700,
              letterSpacing: -2,
              lineHeight: 1,
            }}
          >
            {data.available}
          </span>
          <span
            aria-hidden="true"
            style={{
              fontFamily: F.serif,
              fontSize: 18,
              color: C.inkSoft,
              fontWeight: 600,
            }}
          >
            of {data.allowance} tickets left
          </span>
        </div>

        <ProgressBar
          allowance={data.allowance}
          used={data.used}
          pending={data.pending}
        />

        <CounterRow used={data.used} pending={data.pending} available={data.available} />

        {data.warningOverdrawn && (
          <div
            role="status"
            style={{
              marginTop: 8,
              fontFamily: F.body,
              fontSize: 12,
              color: C.signalDk,
              fontWeight: 700,
            }}
          >
            More visits recorded than your allowance — the membership team will reconcile this.
          </div>
        )}
      </div>

      <DoubleRule style={{ margin: "0 22px" }} />

      <BookletGrid allowance={data.allowance} used={data.used} pending={data.pending} />

      <Footer member={data.member} />
    </section>
  );
}

function ProgressBar({
  allowance,
  used,
  pending,
}: {
  allowance: number;
  used: number;
  pending: number;
}) {
  const safeAllowance = Math.max(allowance, 1);
  const usedPct = Math.min(100, (used / safeAllowance) * 100);
  const usedPendingPct = Math.min(100, ((used + pending) / safeAllowance) * 100);

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={allowance}
      aria-valuenow={used}
      aria-valuetext={`${used} of ${allowance} used`}
      style={{
        height: 10,
        background: C.paperWarm,
        borderRadius: 6,
        marginTop: 14,
        border: `1px solid ${C.hairline}`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* used+pending hatched layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: `${usedPendingPct}%`,
          background: `repeating-linear-gradient(45deg, ${C.brassLt} 0 6px, ${C.brassGlow} 6px 12px)`,
          opacity: 0.7,
        }}
        aria-hidden="true"
      />
      {/* used opaque layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: `${usedPct}%`,
          background: `linear-gradient(90deg, ${C.brass}, ${C.brassDk})`,
        }}
        aria-hidden="true"
      />
    </div>
  );
}

function CounterRow({ used, pending, available }: { used: number; pending: number; available: number }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: 8,
        fontFamily: F.mono,
        fontWeight: 700,
        fontSize: 11,
        color: C.inkMute,
        letterSpacing: 0.6,
        textTransform: "uppercase",
      }}
    >
      <span>{used} used</span>
      <span>{pending} pending</span>
      <span>{available} available</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BookletGrid — 12 stubs (3 rows of 4) in V1
// ---------------------------------------------------------------------------

type StubState = "used" | "pending" | "available";

function BookletGrid({
  allowance,
  used,
  pending,
}: {
  allowance: number;
  used: number;
  pending: number;
}) {
  const total = Math.max(0, allowance);
  // 4 columns by default; wrap to 6 columns when allowance > 24 (per spec).
  const columns = total > 24 ? 6 : 4;

  const cells: StubState[] = Array.from({ length: total }, (_, i) => {
    if (i < used) return "used";
    if (i < used + pending) return "pending";
    return "available";
  });

  return (
    <div style={{ padding: "14px 22px 12px" }}>
      <ol
        role="list"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 8,
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {cells.map((state, idx) => (
          <Stub key={idx} index={idx + 1} state={state} />
        ))}
      </ol>
      <Legend />
    </div>
  );
}

const STUB_STYLE: Record<
  StubState,
  { background: string; border: string; color: string; weight: 600 | 700 }
> = {
  used: {
    background: `linear-gradient(160deg, ${C.brass}, ${C.brassDk})`,
    border: C.brassDk,
    color: C.brassGlow,
    weight: 700,
  },
  pending: {
    background: `repeating-linear-gradient(45deg, ${C.brassBg} 0 5px, ${C.brassGlow} 5px 10px)`,
    border: C.brassLt,
    color: C.brassDk,
    weight: 700,
  },
  available: {
    background: C.paperSoft,
    border: C.hairline,
    color: C.inkMute,
    weight: 600,
  },
};

function Stub({ index, state }: { index: number; state: StubState }) {
  const s = STUB_STYLE[state];
  const label = `Ticket ${index} — ${state}`;
  return (
    <li
      aria-label={label}
      style={{
        width: "100%",
        aspectRatio: "0.85",
        borderRadius: 6,
        border: `1px solid ${s.border}`,
        background: s.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: F.mono,
        fontSize: 11,
        fontWeight: s.weight,
        color: s.color,
        letterSpacing: 0.6,
        textTransform: "uppercase",
      }}
    >
      <span aria-hidden="true">{String(index).padStart(2, "0")}</span>
    </li>
  );
}

function Legend() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        gap: 14,
        justifyContent: "center",
        marginTop: 10,
        fontFamily: F.mono,
        fontWeight: 700,
        fontSize: 10,
        color: C.inkMute,
        letterSpacing: 0.6,
        textTransform: "uppercase",
      }}
    >
      <Swatch state="used" label="USED" />
      <Swatch state="pending" label="PENDING" />
      <Swatch state="available" label="REMAINING" />
    </div>
  );
}

function Swatch({ state, label }: { state: StubState; label: string }) {
  const s = STUB_STYLE[state];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: s.background,
          border: `1px solid ${s.border}`,
        }}
      />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Hero footer — member name + ID stamped in mono
// ---------------------------------------------------------------------------

function Footer({ member }: { member: MemberBlock }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 22px 16px",
        borderTop: `1px dashed ${C.brassLt}`,
      }}
    >
      <div
        style={{
          fontFamily: F.mono,
          fontSize: 12,
          color: C.inkSoft,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: "uppercase",
        }}
      >
        {member.displayName}
      </div>
      <div
        style={{
          fontFamily: F.mono,
          fontSize: 12,
          color: C.inkSoft,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: "uppercase",
        }}
      >
        ID {member.memberIdNumber}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoryCard
// ---------------------------------------------------------------------------

function HistoryCard({ year, history }: { year: string; history: HistoryItem[] }) {
  return (
    <Card padded={false}>
      <div style={{ padding: "12px 16px 4px" }}>
        <Eyebrow>YOUR HISTORY</Eyebrow>
      </div>
      <Hairline style={{ margin: "4px 16px" }} />
      {history.length === 0 ? (
        <div
          style={{
            padding: "22px 16px",
            textAlign: "center",
            fontFamily: F.body,
            fontSize: 13,
            color: C.inkMute,
            fontWeight: 600,
          }}
        >
          No visits recorded for {year} yet.
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {history.map((h, idx) => (
            <HistoryRow key={h.id} item={h} isFirst={idx === 0} />
          ))}
        </ol>
      )}
    </Card>
  );
}

function HistoryRow({ item, isFirst }: { item: HistoryItem; isFirst: boolean }) {
  const dateLabel = formatLongDate(item.date);
  const ticketsLabel = `${item.pax} ticket${item.pax === 1 ? "" : "s"}`;
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        gap: 14,
        padding: "12px 16px",
        alignItems: "center",
        minHeight: 64,
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          background: C.paperWarm,
          border: `1px solid ${C.hairlineLt}`,
          borderRadius: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icons.Ticket s={18} c={C.brassDk} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 15,
            color: C.ink,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: -0.1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {dateLabel}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 12,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 3,
          }}
        >
          {ticketsLabel}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Not-a-member panel
// ---------------------------------------------------------------------------

function NotAMemberPanel() {
  return (
    <div
      style={{
        background: C.sageBg,
        border: `1px solid ${C.sageLt}`,
        borderRadius: 18,
        padding: 22,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: F.serif,
          fontSize: 18,
          color: C.ink,
          fontWeight: 700,
        }}
      >
        We can't find your member record.
      </div>
      <div
        style={{
          fontFamily: F.body,
          fontSize: 14,
          color: C.inkSoft,
          fontWeight: 600,
          marginTop: 8,
        }}
      >
        Comp tickets are tied to NYMR membership. If you're a member and this is wrong, contact the
        membership team.
      </div>
      <a
        href="mailto:membership@nymr.co.uk"
        style={{
          display: "inline-block",
          marginTop: 14,
          padding: "10px 18px",
          background: "transparent",
          border: `1px solid ${C.sageDk}`,
          borderRadius: 12,
          color: C.sageDk,
          fontFamily: F.body,
          fontSize: 13,
          fontWeight: 700,
          textDecoration: "none",
          letterSpacing: 0.3,
        }}
      >
        Email membership@nymr.co.uk
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stale / error / skeletons
// ---------------------------------------------------------------------------

function StaleStrip({
  lastSyncedAt,
  onDismiss,
}: {
  lastSyncedAt: string | null;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        background: C.signalBg,
        border: `1px solid rgba(184,58,43,0.18)`,
        borderRadius: 10,
        padding: "8px 12px",
        fontFamily: F.body,
        fontSize: 12,
        fontWeight: 700,
        color: C.signalDk,
      }}
    >
      <span>
        Records last synced {formatRelative(lastSyncedAt)}. New bookings may take up to 4 hours to
        appear.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss stale-data notice"
        style={{
          background: "transparent",
          border: "none",
          fontFamily: F.mono,
          fontSize: 16,
          color: C.signalDk,
          cursor: "pointer",
          fontWeight: 700,
        }}
      >
        ×
      </button>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div
      style={{
        background: C.signalBg,
        border: `1px solid rgba(184,58,43,0.25)`,
        borderRadius: 14,
        padding: 16,
        fontFamily: F.body,
        fontWeight: 700,
        color: C.signalDk,
      }}
    >
      Couldn't load your tickets.
      <div style={{ marginTop: 4, fontWeight: 600, fontSize: 12, color: C.inkSoft }}>{message}</div>
    </div>
  );
}

function SkeletonStack() {
  return (
    <>
      <SkeletonRow h={300} />
      <SkeletonRow h={68} />
      <SkeletonRow h={68} />
      <SkeletonRow h={68} />
    </>
  );
}

function SkeletonRow({ h }: { h: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: h,
        background: C.paperWarm,
        borderRadius: 14,
        border: `1px solid ${C.hairlineLt}`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isOlderThan8h(iso: string | null): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms > 8 * 60 * 60 * 1000;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "an unknown time ago";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "an unknown time ago";
  const diffMs = Date.now() - ms;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) return "less than an hour ago";
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatLongDate(iso: string): string {
  // Format "2026-05-03" → "03 May 2026" per the spec example ("dd MMM yyyy").
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const mi = parseInt(mo, 10) - 1;
  if (mi < 0 || mi > 11) return iso;
  return `${d} ${SHORT_MONTHS[mi]} ${y}`;
}
